import { useRef, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Paper from 'components/Paper'
import { background } from 'theme/palette'
import { debounce } from 'lodash'
import { useEventListener, useOnClickOutside } from 'utils/hooks'
import { ReactComponent as IconPopoverArrowUp } from 'icons/PopoverArrowUp.svg'
import {
  closePopover,
  removePopover,
} from 'slices/appSlice'

const SIZES = {
  auto: 'max-content',
  xs: '135px',
  sm: '200px',
  md: '320px',
}

const PopoverContainer = styled.div.withConfig({
  shouldForwardProp: prop => ![
    'active',
  ].includes(prop),
})`
  position: relative;
  z-index: ${props => props.theme.zIndex.modal};
  transition: ${props =>
    props.theme.internalMixins.setTransition(['opacity', 'visibility'])};
  ${props =>
    !props.active &&
    `
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  `}
`

const StyledPopover = styled(Paper).attrs({
  round: 'more',
  depth: 2,
})`
  position: fixed;
  // ${props =>
    props.position === 'left'
      ? `left: 100%;`
      : props.position === 'right'
      ? `right: 100%;`
      : props.position === 'bottom'
      ? `top: ${props.y}px;`
      : `bottom: ${props.y}px;`}
  ${props =>
    props.position === 'bottom'
      ? `top: ${props.y}px;`
      : `bottom: ${props.y}px;`}
  ${props => (props.offPage?.length ? '' : `left: ${props.x}px;`)}
  ${props => (props.offPage === 'right' ? 'right: 15px;' : '')}
  ${props =>
    props.offPage?.length
      ? ''
      : `transform: translateX(${
          props.align === 'left'
            ? '0'
            : props.align === 'right'
            ? 'calc(-100% + 20px)'
            : '-50%'
        });`}
  min-height: ${props => (props.sizeY !== 'auto' ? '150px' : 'auto')};
  width: ${props => SIZES[props.sizeX]};
  ${props =>
    props.maxWidth && {
      maxWidth: SIZES[props.maxWidth] || props.maxWidth,
    }}
  transition: ${props => props.theme.internalMixins.setTransition(['width'])};
`

const StyledPopoverInner = styled(Paper).attrs({
  round: 'more',
  sx: {
    overflowX: 'hidden',
  }
})``

const StyledIcon = styled.div`
  position: absolute;
  left: ${props =>
    props.align === 'left' ? '7px' : props.align === 'right' ? 'auto' : '0'};
  right: ${props =>
    props.align === 'left' ? 'auto' : props.align === 'right' ? '7px' : '0'};
  ${props =>
    props.offsetLeft ? `right: calc(${props.offsetLeft + 15}px * 2);` : ''}
  ${props =>
    props.offsetRight
      ? `left: calc(${props.offsetRight + 15}px * 2);`
      : ''}
  margin-left: auto;
  margin-right: auto;
  ${props =>
    props.position === 'bottom'
      ? 'bottom: 100%;'
      : 'top: 100%; transform: scaleY(-1);'}
  display: flex;
  justify-content: center;
  color: ${props => props.theme.palette.background[props.variant].main};
`

const Popover = ({ maxWidth, align, ...props }) => {
  const popoverEl = useRef(null)
  const innerRef = useRef(null)
  const innerRect = innerRef?.current?.getBoundingClientRect() || {}
  const offPage = useRef({})
  const rect = props.target ? props.target.getBoundingClientRect() : null
  const w = rect ? rect.width : 0
  const h = rect ? rect.height : 0
  const xAlign = props.align === 'left' ? w / 2 : 0
  const offsetX = rect ? rect.left - xAlign + props.offsetX : 0
  const offsetYBottom = rect ? rect.top + props.offsetY : 0
  const offsetYTop = rect ? rect.bottom + props.offsetY : 0
  // const pos = ['left', 'right'].includes(props.position) ? props.position : offsetYBottom < window.innerHeight / 2 ? 'bottom' : 'top'
  const pos = offsetYBottom < window.innerHeight / 2 ? 'bottom' : 'top'
  const dispatch = useDispatch()
  const isControlled = typeof props.active !== 'undefined'
  const [active, setActive] = useState(false)
  const isPopoverRemoving = useSelector(
    ({ app }) => app.isPopoverRemoving
  )
  const x = offsetX + w / 2
  const y =
    pos === 'bottom'
      ? offsetYBottom + h + 15
      : window.innerHeight - offsetYTop + h + 15

  if (innerRect.left < 0)
    offPage.current = { side: 'left', amount: 0 - innerRect.left }
  if (innerRect.right > window.innerWidth)
    offPage.current = {
      side: 'right',
      amount: innerRect.right - window.innerWidth,
    }

  useEffect(() => {
    if (!isControlled) setActive(true)
  }, [])

  useEffect(() => {
    if (active) {
      setTimeout(() => {
        innerRef?.current?.focus()
      }, 300)
    }
  }, [active])

  useEffect(() => {
    let removeTimeout = null
    if (!isControlled) {
      if (isPopoverRemoving) {
        setActive(false)
        removeTimeout = setTimeout(() => {
          dispatch(removePopover())
        }, 200)
      }
    }
    return () => {
      if (!isControlled) {
        clearTimeout(removeTimeout)
      }
    }
  }, [isPopoverRemoving])

  useOnClickOutside(popoverEl?.current, () => {
    if (isControlled ? props.active : active) {
      dispatch(closePopover())
      if (props.onClose) props.onClose()
    }
  })

  const renderInner = () => (
    <PopoverContainer
      active={isControlled ? props.active : active}
      ref={popoverEl}
    >
      <StyledPopover
        {...props}
        maxWidth={maxWidth}
        offPage={offPage.current.side}
        position={pos}
        x={x}
        y={y}
        onMouseDown={e => {
          if (props.allowPropagation) e.stopPropagation()
        }}
      >
        <StyledIcon
          {...props}
          position={pos}
          offsetLeft={
            offPage.current.side === 'left' ? offPage.current.amount : undefined
          }
          offsetRight={
            offPage.current.side === 'right'
              ? offPage.current.amount
              : undefined
          }
        >
          <IconPopoverArrowUp />
        </StyledIcon>
        <StyledPopoverInner {...props}>
          <div tabIndex="0" ref={innerRef}>
            {props.children}
          </div>
        </StyledPopoverInner>
      </StyledPopover>
    </PopoverContainer>
  )

  const closePopoverUtil = debounce(
    () => {
      if (!isControlled) dispatch(closePopover())
      props.onClose?.()
      props.onScroll?.()
    },
    500,
    { trailing: false, leading: true }
  )

  useEventListener('scroll', closePopoverUtil)

  if (!props.target) return null

  return renderInner()
}

Popover.propTypes = {
  target: PropTypes.object,
  active: PropTypes.bool,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  align: PropTypes.oneOf(['left', 'center', 'right']),
  onClose: PropTypes.func,
  onScroll: PropTypes.func,
  variant: PropTypes.oneOf(Object.keys(background)),
  maxWidth: PropTypes.any,
  sizeX: PropTypes.oneOf(Object.keys(SIZES)),
  sizeY: PropTypes.oneOf(['auto', 'sm']),
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  allowPropagation: PropTypes.bool,
}

Popover.defaultProps = {
  position: 'bottom',
  align: 'center',
  // active: false,
  variant: 'paper',
  sizeX: 'sm',
  sizeY: 'auto',
  maxWidth: 'calc(100vw - 30px)',
  offsetX: 0,
  offsetY: 0,
}

export default Popover
